<script setup>
import { ref, computed, watch, onMounted } from "vue";
import Swal from "sweetalert2";
import { getHistorialConsumoMP, getDetalleConsumoMP } from "@/utils/api_formularios";
import { exportarConsumoMateriaPrima } from "@/utils/api_materia_prima";
import { usePaginacionConsumoMPStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import CrudFechaRango from "@/components/Shared/CrudFechaRango.vue";

import MPTabla from "../MateriaPrima/MPTabla.vue";
import MPCardsMobile from "../MateriaPrima/MPCardsMobile.vue";
import MPModalDetalle from "../MateriaPrima/MPModalDetalle.vue";

const paginacionStore = usePaginacionConsumoMPStore();

// ── Estado ─────────────────────────────────────────────────
const registros = ref([]);
const loading = ref(false);
const exportando = ref(false);
const detalle = ref(null);
const modalRef = ref(null);

// ── Filtros ────────────────────────────────────────────────
const search = ref("");
const fechaInicio = ref("");
const fechaFin = ref("");
const paginaActual = ref(1);
const filtroMaquinaSet = ref(new Set());
const filtroAditivoSet = ref(new Set());

// Solo exportable si hay rango de fechas completo
const puedeExportar = computed(() => !!fechaInicio.value && !!fechaFin.value);

// ── Ordenamiento ───────────────────────────────────────────
const sortKey = ref("fecha");
const sortDir = ref("desc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

let debounceTimer = null;
watch(search, () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    paginaActual.value = 1;
    cargar();
  }, 350);
});

watch([fechaInicio, fechaFin], () => {
  paginaActual.value = 1;
  cargar();
});

watch([filtroMaquinaSet, filtroAditivoSet], () => {
  paginaActual.value = 1;
});

// ── Carga de datos ─────────────────────────────────────────
async function cargar() {
  loading.value = true;
  try {
    const params = {};
    if (search.value) params.search = search.value;
    if (fechaInicio.value) params.fecha_inicio = fechaInicio.value;
    if (fechaFin.value) params.fecha_fin = fechaFin.value;
    const data = await getHistorialConsumoMP(params);
    registros.value = Array.isArray(data) ? data : [];
  } catch {
    registros.value = [];
    Swal.fire("Error", "No se pudieron cargar los registros", "error");
  } finally {
    loading.value = false;
  }
}

async function verDetalle(item) {
  try {
    const data = await getDetalleConsumoMP(item.id);
    detalle.value = data;
    modalRef.value.show(); // ← llama al método expuesto
  } catch {
    Swal.fire("Error", "No se pudo cargar el detalle", "error");
  }
}

async function exportar() {
  if (!puedeExportar.value) return;
  exportando.value = true;
  try {
    await exportarConsumoMateriaPrima(fechaInicio.value, fechaFin.value);
  } catch {
    Swal.fire("Error", "No se pudo generar el archivo Excel. Intenta de nuevo.", "error");
  } finally {
    exportando.value = false;
  }
}

// ── Filtro + paginación ──────────────────────────────────────
const registrosParaFiltro = computed(() =>
  registros.value.map((r) => ({ ...r, aditivo_label: r.con_aditivo ? "Sí" : "No" }))
);

const registrosFiltrados = computed(() =>
  registrosParaFiltro.value.filter(
    (r) =>
      (!filtroMaquinaSet.value.size || filtroMaquinaSet.value.has(r.maquina_nombre)) &&
      (!filtroAditivoSet.value.size || filtroAditivoSet.value.has(r.aditivo_label))
  )
);

const ordenados = computed(() =>
  [...registrosFiltrados.value].sort((a, b) => {
    const cmp = String(a[sortKey.value] ?? "").localeCompare(
      String(b[sortKey.value] ?? ""),
      "es",
      { sensitivity: "base" }
    );
    return sortDir.value === "asc" ? cmp : -cmp;
  })
);

const porPagina = computed(() => paginacionStore.porPagina);
const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(ordenados.value.length / porPagina.value))
);
const paginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return ordenados.value.slice(inicio, inicio + porPagina.value);
});
const rangoInicio = computed(() =>
  ordenados.value.length === 0 ? 0 : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, ordenados.value.length)
);
const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

function cambiarPagina(n) {
  paginaActual.value = n;
}

function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

onMounted(() => {
  cargar();
});
</script>

<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Materia Prima"
      v-model="search"
      searchPlaceholder="Buscar por operador, máquina, producto..."
    >
      <template #extra>
        <CrudFechaRango
          v-model:fechaInicio="fechaInicio"
          v-model:fechaFin="fechaFin"
        />
      </template>
      <template #actions>
        <button
          class="btn-exportar"
          :disabled="!puedeExportar || exportando"
          @click="exportar"
          title="Exportar a Excel"
        >
          <span v-if="exportando" class="spinner-border spinner-border-sm"></span>
          <i v-else class="bi bi-file-earmark-excel-fill"></i>
          <span class="btn-exportar-texto ms-2">Exportar</span>
        </button>
      </template>
    </CrudToolbar>

    <MPTabla
      :paginados="paginados"
      :loading="loading"
      :paginaActual="paginaActual"
      :totalPaginas="totalPaginas"
      :paginasVisibles="paginasVisibles"
      :rangoInicio="rangoInicio"
      :rangoFin="rangoFin"
      :totalRegistros="ordenados.length"
      :items="registrosParaFiltro"
      :sortKey="sortKey"
      :sortDir="sortDir"
      v-model:filtroMaquinaSet="filtroMaquinaSet"
      v-model:filtroAditivoSet="filtroAditivoSet"
      @ver-detalle="verDetalle"
      @sort="onSort"
      @cambiar-pagina="cambiarPagina"
      @cambiar-por-pagina="cambiarPorPagina"
    />

    <MPCardsMobile
      :paginados="paginados"
      :loading="loading"
      :paginaActual="paginaActual"
      :totalPaginas="totalPaginas"
      :rangoInicio="rangoInicio"
      :rangoFin="rangoFin"
      :totalRegistros="ordenados.length"
      @ver-detalle="verDetalle"
      @cambiar-pagina="cambiarPagina"
      @cambiar-por-pagina="cambiarPorPagina"
    />

    <MPModalDetalle :detalle="detalle" ref="modalRef" />
  </div>
</template>

<style scoped>
.btn-exportar {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 14px;
  background: var(--d-green);
  color: var(--d-cream);
  border: none;
  border-radius: var(--d-radius-pill);
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.2s ease;
}
.btn-exportar:hover:not(:disabled) {
  background: var(--d-green-dark);
}
.btn-exportar:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (max-width: 991.98px) {
  .btn-exportar-texto {
    display: none;
  }
  .btn-exportar {
    width: 32px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}
</style>
